<template>
  <div class="insights-output">
    <InsightsIntegrationsTabs v-if="hasActiveIntegration" />
    <InsightsGitHubIntegration
      v-if="!loading && hasActiveIntegration"
      class="mt-20"
    />
    <div
      v-if="!hasActiveIntegration && !loading"
      class="no-integrations"
    >
      <section class="info">
        <h2>Work output</h2>
        <p class="description">
          Measure key metrics of your team members based on the main apps they use to work and stay tuned to their work
          output performance
        </p>
        <img
          class="mt-20"
          width="394"
          :src="outputPlaceholder.src"
          :alt="outputPlaceholder.alt"
        />
        <div class="warning mt-20">
          <i class="hi hi-warning mr-10"></i>
          <span>The only integration available at the moment is GitHub</span>
        </div>
        <div class="action-buttons mt-20">
          <AppButton @click="toggleDialog(true)">Request other integration</AppButton>
          <a
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
            :data-method="integrationActionMethod"
            :href="GHIntegrationURL"
          >
            {{ integrationAction }} GitHub integration
          </a>
        </div>
      </section>
    </div>
    <AppDialog
      v-if="showDialog"
      :show="showDialog"
      title="Request integrations"
      size="medium"
      append-to-body
      @close="toggleDialog(false)"
    >
      <label class="control-label">Integrations</label>
      <ValidationProvider
        v-slot="{ errors }"
        ref="requestIntegrations"
        rules="required"
      >
        <div :class="{ 'has-error': errors[0] }">
          <AppSelect
            v-model="requestIntegrations"
            :options="requestIntegrationList"
            :select2-options="select2Options"
          />
        </div>
      </ValidationProvider>
      <div
        v-if="showIntegrationDetails"
        class="form-group other-input mt-10"
      >
        <label class="control-label"> Other details </label>
        <textarea
          v-model="requestDetails"
          class="form-control"
          rows="5"
          placeholder="Please tell us what other integration you’d like."
        ></textarea>
      </div>
      <div
        class="action-buttons text-right"
        :style="detailsWhitespaceStyle"
      >
        <AppButton
          type="primary"
          @click="onRequestIntegrations"
        >
          Request
        </AppButton>
      </div>
    </AppDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { INSIGHTS, INSIGHTS_ASSETS as insightsAssets } from '~/app_constants';
import InsightsIntegrationsTabs from '~/components/insights/InsightsIntegrationsTabs.vue';

export default {
  components: {
    InsightsIntegrationsTabs,
    InsightsGitHubIntegration: () => import('~/components/insights/InsightsGitHubIntegration.vue'),
  },
  data: () => ({
    outputPlaceholder: insightsAssets.insights_output_no_data,
    requestIntegrationList: INSIGHTS.integrations.github.requestIntegrationList,
    showDialog: false,
    requestIntegrations: [],
    requestDetails: undefined,
    hasActiveIntegration: false,
    integrationIsDisabled: false,
    GHIntegrationURL: '#',
    integrationActionMethod: 'post',
    select2Options: Object.freeze({
      allowClear: true,
      multiple: true,
      width: '100%',
      placeholder: 'Select the integrations you’d like to have',
    }),
  }),
  computed: {
    ...mapGetters(['loadingWidgets', 'gitHosting', 'organizationId']),
    loading() {
      return this.loadingWidgets.git_hostings;
    },
    integrationAction() {
      return this.integrationIsDisabled ? 'Enable' : 'Add';
    },
    showIntegrationDetails() {
      return this.requestIntegrations.includes('other');
    },
    detailsWhitespaceStyle() {
      // custom number = label + textarea height
      return !this.showIntegrationDetails && 'margin-top: 173px';
    },
  },
  async created() {
    await this.fetchIntegrations();
    window.analytics?.track('Insights output shown');
  },
  methods: {
    ...mapActions(['fetchWidgets']),
    // We will have a separate endpoint for fetching available integrations in the milestone 2
    // Up until then we need to fetch it as a widget, check whether the GH is active or not, fetch the GH data
    async fetchIntegrations() {
      const availableIntegrations = await this.fetchWidgets({
        widgetKey: INSIGHTS.api.gitHubIntegration.hosting,
        skippableAbortParams: ['limit', 'integration_type'],
      });

      if (!availableIntegrations || !availableIntegrations.git_hostings?.github) {
        this.hasActiveIntegration = false;

        return;
      }

      const { status, url, method } = availableIntegrations.git_hostings.github;

      if (['not_exist', 'disabled'].includes(status)) {
        this.hasActiveIntegration = false;
        this.integrationIsDisabled = status === 'disabled';
        this.GHIntegrationURL = url;
        this.integrationActionMethod = method;
      } else if (status === 'active') {
        [INSIGHTS.api.gitHubIntegration.hosting, INSIGHTS.api.gitHubIntegration.table].forEach((widgetKey) => {
          this.fetchWidgets({
            widgetKey,
            extraQueryParams: {
              integration_type: 'github',
            },
          });
        });

        this.hasActiveIntegration = true;
      }
    },
    toggleDialog(showDialog) {
      this.showDialog = showDialog;
    },
    async onRequestIntegrations() {
      const { valid } = await this.$refs.requestIntegrations.validate();

      if (!valid) {
        return;
      }
      window.analytics?.track('Request Adding New Integration', {
        type: 'git_hostings',
        integrations: [...this.requestIntegrations],
        details: this.requestDetails,
        orgId: this.organizationId,
      });
      this.$appMessage('Integration requested');
      this.requestIntegrations = [];
      this.requestDetails = undefined;
      this.showDialog = false;
    },
  },
};
</script>
